import {createSelector} from "reselect";
import {jobFunctionOptionsSelector} from "store/search/searchSelectors";
import {TRootState} from "types";
import {
	STJobFunction,
	STSpecialism,
	TJobFunctionPosition,
	TJobFunctionPositionConnectivityLogic,
	TSpecialismTag,
} from "api/filterOptionsApi/JobFunctionApi/types";
import {TFilterReducerState} from "store/filter/types";

export const jobFunctionFilterConfigSelector = (state: TRootState) =>
	(state.filter as TFilterReducerState).candidate_job_function_filter_config;

export const jobFunctionCandidatePositionSelector = (state: TRootState) =>
	(state.filter as TFilterReducerState).candidate_job_function_filter_config.active_position;

export const currentPositionConnectivityLogicSelector = (state: TRootState) =>
	(state.filter as TFilterReducerState).candidate_job_function_filter_config
		.active_current_position_connect_logic;

export const previousPositionConnectivityLogicSelector = (state: TRootState) =>
	(state.filter as TFilterReducerState).candidate_job_function_filter_config
		.active_previous_position_connect_logic;

export const anyPositionConnectivityLogicSelector = (state: TRootState) =>
	(state.filter as TFilterReducerState).candidate_job_function_filter_config
		.active_any_position_connect_logic;

export const currentAndSpecialismsSelector = (state: TRootState) =>
	(state.filter as TFilterReducerState).candidate_filters.current_specialisms_and;
export const currentOrSpecialismsSelector = (state: TRootState) =>
	(state.filter as TFilterReducerState).candidate_filters.current_specialisms_or;
export const currentNotSpecialismsSelector = (state: TRootState) =>
	(state.filter as TFilterReducerState).candidate_filters.current_specialisms_not;

export const previousAndSpecialismsSelector = (state: TRootState) =>
	(state.filter as TFilterReducerState).candidate_filters.previous_specialisms_and;
export const previousOrSpecialismsSelector = (state: TRootState) =>
	(state.filter as TFilterReducerState).candidate_filters.previous_specialisms_or;
export const previousNotSpecialismsSelector = (state: TRootState) =>
	(state.filter as TFilterReducerState).candidate_filters.previous_specialisms_not;

export const anyAndSpecialismsSelector = (state: TRootState) =>
	(state.filter as TFilterReducerState).candidate_filters.specialisms_and;
export const anyOrSpecialismsSelector = (state: TRootState) =>
	(state.filter as TFilterReducerState).candidate_filters.specialisms_or;
export const anyNotSpecialismsSelector = (state: TRootState) =>
	(state.filter as TFilterReducerState).candidate_filters.specialisms_not;

const getJobFunctionPosition = (_: TRootState, position: TJobFunctionPosition) => position;
const getJobFunctionPositionConnectLogic = (
	_: TRootState,
	__: TRootState,
	logic: TJobFunctionPositionConnectivityLogic,
) => logic;

export const specialismsByPositionSelector = createSelector(
	[
		currentAndSpecialismsSelector,
		currentOrSpecialismsSelector,
		currentNotSpecialismsSelector,
		previousAndSpecialismsSelector,
		previousOrSpecialismsSelector,
		previousNotSpecialismsSelector,
		anyAndSpecialismsSelector,
		anyOrSpecialismsSelector,
		anyNotSpecialismsSelector,
		getJobFunctionPosition,
		getJobFunctionPositionConnectLogic,
	],
	(
		currentAndSpecialisms,
		currentOrSpecialisms,
		currentNotSpecialisms,
		previousAndSpecialisms,
		previousOrSpecialisms,
		previousNotSpecialisms,
		anyAndSpecialisms,
		anyOrSpecialisms,
		anyNotSpecialisms,
		position,
		logic,
	) => {
		if (position === "current" && logic === "and") {
			return currentAndSpecialisms || [];
		} else if (position === "current" && logic === "or") {
			return currentOrSpecialisms || [];
		} else if (position === "current" && logic === "not") {
			return currentNotSpecialisms || [];
		} else if (position === "previous" && logic === "and") {
			return previousAndSpecialisms || [];
		} else if (position === "previous" && logic === "or") {
			return previousOrSpecialisms || [];
		} else if (position === "previous" && logic === "not") {
			return previousNotSpecialisms || [];
		} else if (position === "any" && logic === "and") {
			return anyAndSpecialisms || [];
		} else if (position === "any" && logic === "or") {
			return anyOrSpecialisms || [];
		} else {
			return anyNotSpecialisms || [];
		}
	},
);

export const jobFunctionSelectionTreeSelector = createSelector(
	specialismsByPositionSelector,
	jobFunctionOptionsSelector,
	(specialisms, options: []) => {
		const speFunc: Map<number, number> = new Map();
		const tree: Record<number, number[]> = {};

		options.forEach((o: STJobFunction) => {
			o.specialisms.forEach((s) => {
				if (specialisms.includes(s.id)) {
					speFunc.set(s.id, o.id);
				}
			});
		});

		// Keep it sorted in order to allSelected comparison to work by using _.isEqual.
		specialisms.forEach((id: number) => {
			const functionId = speFunc.get(id);

			if (functionId !== undefined) {
				tree[functionId] = [...(tree[functionId] || []), id].sort();
			}
		});

		return tree;
	},
);

export const currentJobFunctionTagsSelector = createSelector(
	[
		currentAndSpecialismsSelector,
		currentOrSpecialismsSelector,
		currentNotSpecialismsSelector,
		jobFunctionOptionsSelector,
	],
	(andSpecialisms: number[], orSpecialisms: number[], notSpecialisms: number[], options: []) => {
		const current: TSpecialismTag[] = [];

		// it's okay to merge these 3 selection for now, as only one of the current position have value;
		const specialisms = [...andSpecialisms, ...orSpecialisms, ...notSpecialisms];

		const sSet: Set<number> = new Set(specialisms);

		options.forEach((o: STJobFunction) => {
			o.specialisms.forEach((s: STSpecialism) => {
				if (sSet.has(s.id)) {
					current.push({
						id: s.id,
						name: s.name,
						type: "specialism",
						jobFunctionId: o.id,
						jobFunctionName: o.name,
					});
				}
			});
		});

		return current;
	},
);
export const previousJobFunctionTagsSelector = createSelector(
	[
		previousAndSpecialismsSelector,
		previousOrSpecialismsSelector,
		previousNotSpecialismsSelector,
		jobFunctionOptionsSelector,
	],
	(andSpecialisms: number[], orSpecialisms: number[], notSpecialisms: number[], options: []) => {
		const previous: TSpecialismTag[] = [];

		// it's okay to merge these 3 selection for now, as only one of the current position have value;
		const specialisms = [...andSpecialisms, ...orSpecialisms, ...notSpecialisms];

		const sSet: Set<number> = new Set(specialisms);

		options.forEach((o: STJobFunction) => {
			o.specialisms.forEach((s: STSpecialism) => {
				if (sSet.has(s.id)) {
					previous.push({
						id: s.id,
						name: s.name,
						type: "specialism",
						jobFunctionId: o.id,
						jobFunctionName: o.name,
					});
				}
			});
		});

		return previous;
	},
);
export const anyJobFunctionTagsSelector = createSelector(
	[
		anyAndSpecialismsSelector,
		anyOrSpecialismsSelector,
		anyNotSpecialismsSelector,
		jobFunctionOptionsSelector,
	],
	(andSpecialisms: number[], orSpecialisms: number[], notSpecialisms: number[], options: []) => {
		const any: TSpecialismTag[] = [];

		// it's okay to merge these 3 selection for now, as only one of the current position have value;
		const specialisms = [...andSpecialisms, ...orSpecialisms, ...notSpecialisms];

		const sSet: Set<number> = new Set(specialisms);

		options.forEach((o: STJobFunction) => {
			// if (jfSet.has(o.id)) {
			// 	any.push({id: o.id, name: o.name, type: "job_function"});
			// }

			o.specialisms.forEach((s: STSpecialism) => {
				if (sSet.has(s.id)) {
					any.push({
						id: s.id,
						name: s.name,
						type: "specialism",
						jobFunctionId: o.id,
						jobFunctionName: o.name,
					});
				}
			});
		});

		return any;
	},
);

export const specialismFilterKeyByPosition = createSelector(
	getJobFunctionPosition,
	getJobFunctionPositionConnectLogic,
	(position, logic) => {
		if (position === "current" && logic === "and") {
			return "current_specialisms_and";
		} else if (position === "current" && logic === "or") {
			return "current_specialisms_or";
		} else if (position === "current" && logic === "not") {
			return "current_specialisms_not";
		} else if (position === "previous" && logic === "and") {
			return "previous_specialisms_and";
		} else if (position === "previous" && logic === "or") {
			return "previous_specialisms_or";
		} else if (position === "previous" && logic === "not") {
			return "previous_specialisms_not";
		} else if (position === "any" && logic === "and") {
			return "specialisms_and";
		} else if (position === "any" && logic === "or") {
			return "specialisms_or";
		} else {
			return "specialisms_not";
		}
	},
);

export const allSpecialismsCountSelector = createSelector(
	[
		currentAndSpecialismsSelector,
		currentOrSpecialismsSelector,
		currentNotSpecialismsSelector,
		previousAndSpecialismsSelector,
		previousOrSpecialismsSelector,
		previousNotSpecialismsSelector,
		anyAndSpecialismsSelector,
		anyOrSpecialismsSelector,
		anyNotSpecialismsSelector,
	],
	(
		currentAndSpecialisms,
		currentOrSpecialisms,
		currentNotSpecialisms,
		previousAndSpecialisms,
		previousOrSpecialisms,
		previousNotSpecialisms,
		anyAndSpecialisms,
		anyOrSpecialisms,
		anyNotSpecialisms,
	) =>
		currentAndSpecialisms.length +
		currentOrSpecialisms.length +
		currentNotSpecialisms.length +
		previousAndSpecialisms.length +
		previousOrSpecialisms.length +
		previousNotSpecialisms.length +
		anyAndSpecialisms.length +
		anyOrSpecialisms.length +
		anyNotSpecialisms.length,
);

export const allSpecialismIdsSelector = createSelector(
	[
		currentAndSpecialismsSelector,
		currentOrSpecialismsSelector,
		currentNotSpecialismsSelector,
		previousAndSpecialismsSelector,
		previousOrSpecialismsSelector,
		previousNotSpecialismsSelector,
		anyAndSpecialismsSelector,
		anyOrSpecialismsSelector,
		anyNotSpecialismsSelector,
	],
	(
		currentAndSpecialisms,
		currentOrSpecialisms,
		currentNotSpecialisms,
		previousAndSpecialisms,
		previousOrSpecialisms,
		previousNotSpecialisms,
		anyAndSpecialisms,
		anyOrSpecialisms,
		anyNotSpecialisms,
	) => [
		...currentAndSpecialisms,
		...currentOrSpecialisms,
		...currentNotSpecialisms,
		...previousAndSpecialisms,
		...previousOrSpecialisms,
		...previousNotSpecialisms,
		...anyAndSpecialisms,
		...anyOrSpecialisms,
		...anyNotSpecialisms,
	],
);
